/**
 * Contract List Component
 * Szerződések listázása és kezelése
 */

import React, { useState } from 'react';
import { CustomerContract, CONTRACT_STATUS_OPTIONS } from '../../types/customerContract';
import { useCustomerContracts } from '../../hooks/useCustomerContracts';
import { Timestamp } from 'firebase/firestore';
import ContractEditor from './ContractEditor';
import ContractView from './ContractView';
import {
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { formatDate } from '../../utils/dateHelpers';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface ContractListProps {
  customerId?: string; // Ha meg van adva, csak egy ügyfél szerződései
  customerName?: string; // Ügyfél neve a szerződés editorhoz
}

// Helper function to convert Firestore Timestamp to Date
const toDate = (value: any): Date => {
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  return new Date(value);
};

const ContractList: React.FC<ContractListProps> = ({ customerId, customerName }) => {
  const { contracts, loading, stats, createContract, updateContract, deleteContract } = useCustomerContracts(customerId);
  
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<CustomerContract | null>(null);
  const [contractToDelete, setContractToDelete] = useState<CustomerContract | null>(null);

  // Státusz badge színek
  const getStatusColor = (status: string) => {
    const option = CONTRACT_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || 'gray';
  };

  const getStatusBadgeClass = (status: string) => {
    const color = getStatusColor(status);
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200 dark:border-green-700',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border-red-200 dark:border-red-700',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-600'
    };
    return colorMap[color] || colorMap.gray;
  };

  // Új szerződés
  const handleCreate = () => {
    setSelectedContract(null);
    setIsEditorOpen(true);
  };

  // Szerkesztés
  const handleEdit = (contract: CustomerContract) => {
    setSelectedContract(contract);
    setIsEditorOpen(true);
  };

  // Megtekintés
  const handleView = (contract: CustomerContract) => {
    setSelectedContract(contract);
    setIsViewOpen(true);
  };

  // Törlés
  const handleDelete = (contract: CustomerContract) => {
    setContractToDelete(contract);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (contractToDelete) {
      await deleteContract(contractToDelete.id);
      setIsDeleteOpen(false);
      setContractToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header és statisztikák */}
      {!customerId && stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Összes szerződés</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalContracts}</p>
              </div>
              <DocumentTextIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Aktív szerződések</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.activeContracts}</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Hamarosan lejár</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.expiringSoon}</p>
              </div>
              <ClockIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Havi bevétel</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.totalMonthlyRevenue.toLocaleString()} Ft
                </p>
              </div>
              <CurrencyDollarIcon className="h-8 w-8 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Új szerződés gomb */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Szerződések ({contracts.length})
        </h2>
        {customerId && (
          <button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Új szerződés
          </button>
        )}
      </div>

      {/* Szerződések listája */}
      {contracts.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Még nincs szerződés
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Hozd létre az első szerződést az ügyfélhez
          </p>
          {customerId && (
            <button
              onClick={handleCreate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Új szerződés
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Szerződés
                  </th>
                  {!customerId && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ügyfél
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Típus
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Időtartam
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Havi díj
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Státusz
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {contracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {contract.contractName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {contract.contractNumber}
                        </div>
                        {contract.generatedFrom && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            📄 {contract.generatedFrom.sourceName}
                          </div>
                        )}
                      </div>
                    </td>
                    {!customerId && (
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {contract.customerName}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {contract.contractType === 'fixed_term' && '📅 Határozott'}
                        {contract.contractType === 'indefinite' && '♾️ Határozatlan'}
                        {contract.contractType === 'project_based' && '🎯 Projekt'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(contract.startDate)}
                        {contract.endDate && (
                          <span className="text-gray-500 dark:text-gray-400">
                            {' - '}
                            {formatDate(contract.endDate)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {contract.monthlyFee.toLocaleString()} {contract.currency}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {contract.billingFrequency === 'monthly' && '📆 Havi'}
                        {contract.billingFrequency === 'quarterly' && '📊 Negyedéves'}
                        {contract.billingFrequency === 'yearly' && '📅 Éves'}
                        {contract.billingFrequency === 'hourly' && '⏱️ Óradíjas'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClass(contract.status)}`}>
                        {CONTRACT_STATUS_OPTIONS.find(opt => opt.value === contract.status)?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleView(contract)}
                          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Megtekintés"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(contract)}
                          className="text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          title="Szerkesztés"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(contract)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title="Törlés"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {isEditorOpen && customerId && (
        <ContractEditor
          isOpen={isEditorOpen}
          onClose={() => {
            setIsEditorOpen(false);
            setSelectedContract(null);
          }}
          onSubmit={async (data) => {
            if (selectedContract) {
              await updateContract(selectedContract.id, data);
            } else {
              const contractId = await createContract(data);
              console.log('Contract created:', contractId);
            }
          }}
          customerId={customerId}
          customerName={customerName || contracts[0]?.customerName || ''}
          initialData={selectedContract ? {
            contractName: selectedContract.contractName,
            contractType: selectedContract.contractType,
            services: selectedContract.services,
            startDate: toDate(selectedContract.startDate),
            endDate: selectedContract.endDate ? toDate(selectedContract.endDate) : undefined,
            monthlyFee: selectedContract.monthlyFee,
            billingFrequency: selectedContract.billingFrequency,
            currency: selectedContract.currency,
            notes: selectedContract.notes,
            terms: selectedContract.terms,
            autoRenew: selectedContract.autoRenew,
            renewalNoticeDays: selectedContract.renewalNoticeDays
          } : undefined}
        />
      )}

      {isViewOpen && selectedContract && (
        <ContractView
          isOpen={isViewOpen}
          onClose={() => {
            setIsViewOpen(false);
            setSelectedContract(null);
          }}
          contract={selectedContract}
        />
      )}

      {isDeleteOpen && contractToDelete && (
        <ConfirmDeleteModal
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setContractToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Szerződés lezárása"
          deleteMessage={`Biztosan lezárod ezt a szerződést: ${contractToDelete.contractName}?`}
          deleteWarning="A szerződés státusza 'Lezárt'-ra változik."
        />
      )}
    </div>
  );
};

export default ContractList;
